import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Api } from '../api/api';
import {
  dashboardAuthControllerLogin,
  dashboardAuthControllerLogout,
  dashboardAuthControllerGetCurrentUser,
} from '../api/functions';
import type { DashboardLoginDto } from '../api/models';
import { Observable, of, from, tap, concatMap, defer } from 'rxjs';
import { lastValueFrom } from 'rxjs';

/**
 * Dashboard user from login response
 * TODO: Add proper DashboardUser Response DTO in backend
 */
export interface DashboardUser {
  id: string;
  email: string;
  username: string;
  fullName?: string;
  company?: string;
}

/**
 * Authentication service for Dashboard (project owners)
 *
 * Manages authentication state using Angular signals for dashboard users.
 * Dashboard users manage their own projects via dashboard.mosaic.
 *
 * Features:
 * - Signal-based reactive state management
 * - Cookie-based authentication with HTTP-only cookies
 * - Simplified auth (no complex permissions like admin)
 *
 * @example
 * ```typescript
 * // Login
 * await lastValueFrom(authService.login({ email, password }));
 *
 * // Access user
 * const user = authService.userSignal();
 * const isAuth = authService.isAuthenticated();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  // Dependencies
  private readonly api = inject(Api);
  private readonly router = inject(Router);

  // === Signals ===

  /**
   * User state signal - contains authenticated dashboard user data or null
   */
  public readonly userSignal = signal<DashboardUser | null>(null);

  /**
   * API token signal - stores the authentication token
   */
  private readonly _apiToken = signal<string | null>(null);

  // === Computed Signals ===

  /**
   * Computed signal indicating if user is authenticated
   */
  public readonly isAuthenticated = computed(() => this.userSignal() !== null);

  /**
   * Computed signal with user's email
   */
  public readonly userEmail = computed(() => this.userSignal()?.email);

  /**
   * Computed signal with user's username
   */
  public readonly username = computed(() => this.userSignal()?.username);

  // === Public Methods ===

  /**
   * Login with email and password
   *
   * On success:
   * - Stores user in userSignal
   * - Stores apiToken
   * - Backend sets HTTP-only authentication cookie
   * - Returns user data
   *
   * @param credentials - Dashboard login credentials (email, password)
   * @returns Observable with user data
   */
  public login(credentials: DashboardLoginDto): Observable<DashboardUser> {
    return defer(() =>
      from(this.api.invoke(dashboardAuthControllerLogin, { body: credentials }))
    ).pipe(
      tap((response: any) => {
        this.userSignal.set(response.user);
        this._apiToken.set(response.apiToken);
      }),
      // Extract user from response
      concatMap((response: any) => of(response.user as DashboardUser))
    );
  }

  /**
   * Logout the current user
   *
   * Actions:
   * - Calls backend logout endpoint (clears cookie)
   * - Clears user signal on success
   * - Optionally redirects to login page
   *
   * @param redirectToLogin - If true, navigates to /login after logout
   * @returns Observable<void>
   */
  public logout(redirectToLogin: boolean = true): Observable<void> {
    return defer(() => from(this.api.invoke(dashboardAuthControllerLogout, {}))).pipe(
      tap(() => {
        this.userSignal.set(null);
        this._apiToken.set(null);

        if (redirectToLogin) {
          this.router.navigate(['/login']);
        }
      }),
      // Return void
      concatMap(() => of(undefined))
    );
  }

  /**
   * Find user by API token (cookie-based authentication)
   *
   * Features:
   * - Intelligent caching: returns cached user if available
   * - Automatic cookie-based authentication
   * - Errors handled by global interceptor
   *
   * @param forceRefresh - If true, bypasses cache and fetches from API
   * @returns Observable<DashboardUser>
   */
  public findUserByApiToken(forceRefresh: boolean = false): Observable<DashboardUser> {
    return defer(() => {
      const cachedUser = this.userSignal();

      // Return cached user if available and not forcing refresh
      if (cachedUser && !forceRefresh) {
        return of(cachedUser);
      }

      // Fetch from API
      return from(this.api.invoke(dashboardAuthControllerGetCurrentUser, {})).pipe(
        concatMap((response: any) => {
          const user = response as DashboardUser;
          this.userSignal.set(user);
          return of(user);
        })
      );
    });
  }

  /**
   * Initialize authentication state on app startup
   *
   * Attempts to restore user session from cookie by calling findUserByApiToken.
   * Should be called in app initialization (app.config.ts or APP_INITIALIZER)
   * Errors are displayed by global interceptor.
   *
   * @returns Promise<boolean> - true if user is authenticated, false otherwise
   */
  public async initializeAuth(): Promise<boolean> {
    // If already authenticated, return true immediately
    if (this.isAuthenticated()) {
      return true;
    }

    try {
      const user = await lastValueFrom(this.findUserByApiToken());
      const isAuthenticated = !!user;

      return isAuthenticated;
    } catch (error) {
      // Error already displayed by interceptor
      return false;
    }
  }

  /**
   * Clear all authentication state
   * Useful for cleanup or manual state reset
   * Does NOT call backend logout endpoint
   */
  public clearAuthState(): void {
    this.userSignal.set(null);
    this._apiToken.set(null);
  }

  /**
   * Get current API token
   */
  public getApiToken(): string | null {
    return this._apiToken();
  }
}
